import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper, Typography, Box, Button, TextField, Chip, Grid, Divider,
  CircularProgress, FormControl, Select, MenuItem, Switch, FormControlLabel,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import GetAppIcon from '@material-ui/icons/GetApp';
import CsvUpload from './shared/CsvUpload';
import { downloadCsvTemplate } from './shared/helpers';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(1),
    boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
    backgroundColor: 'white',
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  sectionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  sectionIcon: {
    color: '#3f51b5',
    fontSize: 24,
    marginRight: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 400,
    color: '#333',
  },
  sectionSubtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: theme.spacing(3),
  },
  formControl: {
    width: '100%',
    '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' },
  },
  formLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
    marginBottom: theme.spacing(1),
    display: 'block',
  },
  formGroup: {
    marginBottom: theme.spacing(2.5),
  },
  multiSelect: {
    backgroundColor: '#f5f5f5',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#bdbdbd' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3f51b5' },
  },
  countryChip: {
    backgroundColor: '#e0f2f1',
    color: '#00695c',
  },
  slaDaysInput: {
    width: 160,
    '& .MuiOutlinedInput-root': { backgroundColor: '#fff3e0' },
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  primaryButton: {
    backgroundColor: '#3f51b5',
    color: 'white',
    textTransform: 'none',
    padding: theme.spacing(1.5, 3),
    fontSize: '14px',
    fontWeight: 500,
    '&:hover': { backgroundColor: '#303f9f' },
    '&:disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' },
  },
  templateButton: {
    backgroundColor: 'white',
    color: '#333',
    border: '1px solid #e0e0e0',
    textTransform: 'none',
    padding: theme.spacing(1, 2),
    fontSize: '13px',
    '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#bdbdbd' },
  },
}));

const EMPTY_FORM = {
  last_mile_warehouse: '',
  destination_countries: [],
  additional_sla: '',
  status: true,
};

const OptionRuleForm = ({ warehouses, countries, saving, onUpload, getCountryName, showSnack }) => {
  const classes = useStyles();
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [csvFile, setCsvFile] = useState(null);
  const [parsedCount, setParsedCount] = useState(0);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleFileProcessed = (file, ids, errorMsg) => {
    if (errorMsg) {
      showSnack(errorMsg, 'error');
      return;
    }
    setCsvFile(file);
    setParsedCount(ids?.length || 0);
  };

  const isValid =
    form.last_mile_warehouse &&
    csvFile &&
    form.additional_sla !== '' &&
    Number(form.additional_sla) !== 0;

  const handleSubmit = async () => {
    if (!isValid) {
      showSnack('Warehouse, CSV file, and additional SLA are required.', 'error');
      return;
    }
    const fd = new FormData();
    fd.append('file', csvFile);
    fd.append('last_mile_warehouse', form.last_mile_warehouse);
    fd.append('destination_countries', JSON.stringify(form.destination_countries || []));
    fd.append('additional_sla', String(Number(form.additional_sla)));
    fd.append('status', String(Boolean(form.status)));
    const success = await onUpload(fd);
    if (success) {
      setForm({ ...EMPTY_FORM });
      setCsvFile(null);
      setParsedCount(0);
      const el = document.getElementById('option-override-csv-upload');
      if (el) el.value = '';
    }
  };

  return (
    <Paper className={classes.card}>
      <Box className={classes.sectionHeader}>
        <Box className={classes.sectionHeaderLeft}>
          <CloudUploadIcon className={classes.sectionIcon} />
          <Typography className={classes.sectionTitle}>Upload option SLA override batch</Typography>
        </Box>
        <Button
          variant="outlined"
          className={classes.templateButton}
          onClick={downloadCsvTemplate}
          startIcon={<GetAppIcon />}
          size="small"
        >
          Download CSV template
        </Button>
      </Box>
      <Typography className={classes.sectionSubtitle}>
        Upload a CSV with an option ID column (option_id, optionId, or Option ID). One ID per row or comma-separated.
        Leave destination countries empty to match all countries.
      </Typography>
      <Divider style={{ marginBottom: 24 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box className={classes.formGroup}>
            <Typography className={classes.formLabel}>Last mile warehouse *</Typography>
            <FormControl variant="outlined" size="small" className={classes.formControl}>
              <Select
                value={form.last_mile_warehouse}
                onChange={(e) => update('last_mile_warehouse', e.target.value)}
                displayEmpty
                className={classes.multiSelect}
              >
                <MenuItem value=""><em>Select warehouse</em></MenuItem>
                {warehouses.map((wh) => (
                  <MenuItem key={wh.warehouse_id} value={wh.warehouse_id}>
                    {wh.warehouse_name || wh.warehouse_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box className={classes.formGroup}>
            <Typography className={classes.formLabel}>Destination countries</Typography>
            <FormControl variant="outlined" size="small" className={classes.formControl}>
              <Select
                multiple
                value={form.destination_countries}
                onChange={(e) => update('destination_countries', e.target.value)}
                className={classes.multiSelect}
                renderValue={(selected) => (
                  <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {selected.map((code) => (
                      <Chip key={code} label={getCountryName(code)} size="small" className={classes.countryChip} />
                    ))}
                  </Box>
                )}
              >
                {countries.map((c) => (
                  <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={form.status}
                onChange={(e) => update('status', e.target.checked)}
                color="primary"
              />
            }
            label="Active"
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <Box className={classes.formGroup}>
            <Typography className={classes.formLabel}>Option IDs (CSV) *</Typography>
            <CsvUpload
              inputId="option-override-csv-upload"
              file={csvFile}
              parsedCount={parsedCount}
              onFileProcessed={handleFileProcessed}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box className={classes.formGroup}>
            <Typography className={classes.formLabel}>Additional SLA (days) *</Typography>
            <TextField
              variant="outlined"
              size="small"
              type="number"
              placeholder="e.g. 1"
              value={form.additional_sla}
              onChange={(e) => update('additional_sla', e.target.value)}
              className={classes.slaDaysInput}
              inputProps={{ min: 0 }}
            />
          </Box>
        </Grid>
      </Grid>

      <Box className={classes.buttonRow}>
        <Button
          variant="contained"
          className={classes.primaryButton}
          onClick={handleSubmit}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
          disabled={saving || !isValid}
        >
          {saving ? 'Uploading...' : 'Upload batch'}
        </Button>
      </Box>
    </Paper>
  );
};

export default OptionRuleForm;
